/**
 * src/components/voice/VoiceQueueTray.jsx — global voice session tray.
 *
 * Plan: docs/plans/2026-05-01-voice-sessions-and-image-sync-plan.md (V-3)
 *       docs/plans/2026-06-02-voice-upload-progress-ux.md (T1 — direct-upload jobs)
 *
 * Mounts at App level (AppShell.jsx). Renders nothing when there are no active
 * sessions. When sessions exist, anchors to the top of the viewport (CEO
 * 2026-05-01: "top as-is now works"), shows a stack of NoteSessionCards FIFO by
 * created_at.
 *
 * TICKET-UPLOAD-PROGRESS-UX-T1 (CEO 2026-06-02): the SINGLE status surface for
 * ALL voice intake. It merges two sources:
 *   1. IndexedDB queue sessions (live-mic breadcrumbs) via useVoiceQueueState.
 *   2. Direct File→MinIO upload jobs (the "Upload File" button) via
 *      useUploadJobs / uploadStatusStore — mapped to the same card shape with
 *      `direct: true`.
 *
 * CEO 2026-06-21: this is now the ONLY voice-status overlay — the duplicate
 * bottom-right "Processing recording" toast was removed. The tray is MINIMIZABLE:
 * the chevron collapses the card stack to a small pill (the background jobs keep
 * running — minimize, not stop); tapping the pill re-expands. Tab-independent
 * phase advance runs in UploadJobTrackerHost.
 *
 * Auto-fade behavior on 'ready' (5 min) and stickiness on 'error' for IDB
 * sessions live in useVoiceQueueState.
 */
import { useState } from 'react';
import { ChevronUp, ChevronDown, Loader2 } from 'lucide-react';
import NoteSessionCard from './NoteSessionCard.jsx';
import { useVoiceQueueState } from '../../lib/voice/useVoiceQueueState.js';
import { useUploadJobs } from '../../lib/voice/useUploadJobs.js';
import { removeUploadJob } from '../../lib/voice/uploadStatusStore.js';

// Map a direct-upload store job onto the NoteSessionCard session shape. The
// file name is the most meaningful label for a picked file; `direct: true`
// gates off the IDB recovery buttons (there is no IDB blob to download/retry).
function jobToSession(job) {
  return {
    recording_id: job.id,
    scope_type: job.scopeType,
    scope_id: job.scopeId,
    scope_label: job.fileName,
    status: job.phase,
    upload_pct: job.pct,
    chunks_done: job.chunksDone,
    chunks_total: job.chunksTotal,
    headline: job.headline,
    error_message: job.error,
    transcript_raw: null,
    created_at: job.createdAt,
    direct: true,
  };
}

// CEO 2026-05-04 — fadeMs default bumped from 15s to 5 min (a success card
// disappeared before the user could read "Ready" + the transcript link).
export default function VoiceQueueTray({ pollMs, fadeMs = 5 * 60 * 1000, onRetry, fetchProgress } = {}) {
  const { sessions, dismiss } = useVoiceQueueState({ pollMs, fadeMs });
  const uploadJobs = useUploadJobs({ pollMs, fetchProgress });
  const [minimized, setMinimized] = useState(false);

  const directSessions = uploadJobs.map(jobToSession);
  const merged = [...sessions, ...directSessions]
    .sort((a, b) => (a.created_at || 0) - (b.created_at || 0));

  if (!merged.length) return null;

  // Route dismiss to the right source: direct jobs live in uploadStatusStore,
  // IDB sessions in useVoiceQueueState. Dismiss hides the card; the background
  // job continues regardless (it is never cancelled here).
  const directIds = new Set(uploadJobs.map((j) => j.id));
  const handleDismiss = (recordingId) => {
    if (directIds.has(recordingId)) removeUploadJob(recordingId);
    else dismiss(recordingId);
  };
  const anyActive = merged.some((s) => s.status && s.status !== 'ready' && s.status !== 'error');

  return (
    <div
      data-testid="voice-queue-tray"
      style={{
        position: 'fixed',
        top: 'env(safe-area-inset-top, 0px)',
        left: 0, right: 0,
        zIndex: 50,
        display: 'flex', flexDirection: 'column', gap: 6,
        padding: '8px 12px',
        pointerEvents: 'none',
      }}
    >
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 6,
        maxWidth: 520, margin: '0 auto', width: '100%',
        pointerEvents: 'auto',
      }}>
        {minimized ? (
          // Collapsed pill — minimize, not stop. Jobs keep running in the
          // background; tap to re-expand the full status cards.
          <button
            type="button"
            data-testid="voice-queue-expand"
            onClick={() => setMinimized(false)}
            aria-label="Expand"
            style={{
              alignSelf: 'center', display: 'inline-flex', alignItems: 'center', gap: 8,
              minHeight: 44, padding: '8px 16px', borderRadius: 999, border: 'none',
              cursor: 'pointer', background: 'var(--navy-surface)', color: 'var(--on-navy)',
              fontSize: 13, fontWeight: 600, boxShadow: 'var(--shc)',
            }}
          >
            <Loader2 size={15} style={{ animation: anyActive ? 'spin 1s linear infinite' : 'none' }} />
            <span data-testid="voice-queue-count">{merged.length}</span>
            <ChevronDown size={15} />
          </button>
        ) : (
          <>
            <button
              type="button"
              data-testid="voice-queue-minimize"
              onClick={() => setMinimized(true)}
              aria-label="Minimize"
              style={{
                alignSelf: 'flex-end', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                minWidth: 44, minHeight: 44, borderRadius: 999, border: 'none', cursor: 'pointer',
                background: 'var(--glass)', color: 'var(--t2)', boxShadow: 'inset 0 0 0 1px var(--bdr)',
              }}
            >
              <ChevronUp size={16} />
            </button>
            {merged.map((s) => (
              <NoteSessionCard
                key={s.recording_id}
                session={s}
                onDismiss={handleDismiss}
                onRetry={onRetry}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}
